import { socialLogin } from './socialSignOn';
import { GoogleUser, isLegacyGoogleSetting } from './socialSignOnTypes';
import { APIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import { UserIdentity } from './userIdentity';
import { getGoogleAuthInstance } from './initGoogleLogin';
import Identity from './identity';

/** @hidden */
export const deprecationMessage =
  'Legacy Google Sign In will be deprecated on Dec 31, 2022. Use the new Arc Identity & Sign In With Google SDKs.';

/**
 * Intended to be used as a callback to the user action like `onclick` to a custom "Sign in with Google" buttton.
 *
 * When used as a response to a user action (onclick) event, this function will prompt the user to login to Google if they have not already done so. If they have not previously logged in with Google in your application, a new account is created in your application and logged in. If they have previously logged in, the user is simply logged into your app.
 *
 * @returns
 * A __Promise__ resolving to a user's identity. When successful, updates Identity.userIdentity with this information
 *
 */

export const googleSignOn = async (
  googleUser?: GoogleUser
): Promise<APIErrorResponse | UserIdentity | Error> => {
  console.error(deprecationMessage);
  if (googleUser) {
    return signOnWithGoogleUser(googleUser);
  }
  try {
    if (isLegacyGoogleSetting(Identity._google)) {
      if (!Identity._google.authInstance) {
        await getGoogleAuthInstance();
      }
      const authInstance = Identity._google.authInstance;
      if (authInstance && authInstance.isSignedIn.get()) {
        return signOnWithGoogleUser(authInstance.currentUser.get());
      }
      const googleUser = await Identity._google.authInstance?.signIn({
        prompt: 'select_account'
      });
      return signOnWithGoogleUser(googleUser);
    } else {
      const e = new Error(
        '[Arc Identity SDK] - This method will only work with the legacy Google Sign In. Discontinue use of this method.'
      );
      console.error(e);
      throw e;
    }
  } catch (e) {
    throw e;
  }
};

/**
 * Intended to be used for logging in or creating a user in Arc Identity after swg events like `paymentResponse`, `entitlements`, etc.
 *
 * @param idToken the idToken from SwG's paymentResponse. If one is not provided, this SDK will attempt to use the Google auth SDK loaded on the page.
 * @param swgSubscriptionTokenResponse the response from `Sales.checkSwgSubscriptionToken` when it is called to find or create an Arc identity from the subscription token
 *
 * Specify only one or the other. If neither are specified, the Identity SDK will attempt to use the currently signed in Google user in the Google auth2 SDK.
 *
 * @returns
 * A __Promise__ resolving to a user's identity. When successful, updates Identity.userIdentity with this information
 *
 */

export const signOnSwgUser = async ({
  idToken,
  swgSubscriptionTokenResponse
}: {
  idToken?: string;
  swgSubscriptionTokenResponse?: {
    profileId: string;
    accessToken: string;
    refreshToken: string;
    exists: boolean;
  };
}) => {
  console.error(deprecationMessage);

  if (idToken) {
    return socialLogin('google', { credentials: idToken });
  } else if (swgSubscriptionTokenResponse) {
    Identity.userIdentity = {
      uuid: swgSubscriptionTokenResponse.profileId,
      accessToken: swgSubscriptionTokenResponse.accessToken,
      refreshToken: swgSubscriptionTokenResponse.refreshToken
    };
    return Identity.heartbeat();
  } else {
    await getGoogleAuthInstance();
    if (isLegacyGoogleSetting(Identity._google)) {
      if (Identity._google.authInstance?.isSignedIn.get()) {
        const currentUser = Identity._google.authInstance?.currentUser.get();
        const idToken = currentUser.getAuthResponse().id_token;
        return socialLogin('google', { credentials: idToken });
      } else {
        throw new Error('[Arc Identity SDK] - No google user logged in.');
      }
    } else {
      const e = new Error(
        '[Arc Identity SDK] - This method will only work with the legacy Google Sign In. Discontinue use of this method.'
      );
      console.error(e);
      throw e;
    }
  }
};

const signOnWithGoogleUser = (googleUser: any) => {
  console.error(deprecationMessage);

  const googleIdToken = googleUser.getAuthResponse().id_token;
  if (isLegacyGoogleSetting(Identity._google)) {
    return socialLogin('google', {
      credentials: googleIdToken
    })
      .then(userIdentity => {
        if (isLegacyGoogleSetting(Identity._google)) {
          if (Identity._google.onSuccess) {
            Identity._google.onSuccess();
          }
          return userIdentity;
        } else {
          throw new Error(
            '[Arc Identity SDK] - This method will only work with the legacy Google Sign In. Discontinue use of this method.'
          );
        }
      })
      .catch(e => {
        if (isLegacyGoogleSetting(Identity._google)) {
          if (Identity._google.onFailure) {
            Identity._google.onFailure();
          }
          throw e;
        } else {
          throw new Error(
            '[Arc Identity SDK] - This method will only work with the legacy Google Sign In. Discontinue use of this method.'
          );
        }
      });
  } else {
    throw new Error(
      '[Arc Identity SDK] - This method will only work with the legacy Google Sign In. Discontinue use of this method.'
    );
  }
};

export default googleSignOn;
